import { useCallback, useEffect, useRef, useState } from 'react';

import { connectionApi, ConnectionApiError } from './connection-api';
import type { ConnectionPage, WorkspaceConnectionSummary } from './types';

const EMPTY_PAGE: ConnectionPage = { items: [], nextCursor: null };
const tenantCache = new Map<string, ConnectionPage>();
const tenantListeners = new Map<string, Set<(page: ConnectionPage) => void>>();

function publish(
  workspaceId: string,
  update: (current: ConnectionPage) => ConnectionPage
) {
  const next = update(tenantCache.get(workspaceId) ?? EMPTY_PAGE);
  tenantCache.set(workspaceId, next);
  tenantListeners.get(workspaceId)?.forEach(listener => listener(next));
}

function subscribe(
  workspaceId: string,
  listener: (page: ConnectionPage) => void
) {
  const listeners = tenantListeners.get(workspaceId) ?? new Set();
  listeners.add(listener);
  tenantListeners.set(workspaceId, listeners);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) tenantListeners.delete(workspaceId);
  };
}

export function useConnections(workspaceId: string) {
  const generation = useRef(0);
  const [snapshot, setSnapshot] = useState<{
    workspaceId: string;
    page: ConnectionPage;
  }>({
    workspaceId,
    page: tenantCache.get(workspaceId) ?? EMPTY_PAGE,
  });
  const page =
    snapshot.workspaceId === workspaceId
      ? snapshot.page
      : (tenantCache.get(workspaceId) ?? EMPTY_PAGE);
  const [request, setRequest] = useState<{
    workspaceId: string;
    loading: boolean;
    loadingMore: boolean;
    error?: string;
    forbidden: boolean;
  }>({ workspaceId, loading: true, loadingMore: false, forbidden: false });
  const activeRequest =
    request.workspaceId === workspaceId
      ? request
      : {
          workspaceId,
          loading: true,
          loadingMore: false,
          forbidden: false,
          error: undefined,
        };

  const reload = useCallback(async () => {
    const current = ++generation.current;
    setRequest({
      workspaceId,
      loading: true,
      loadingMore: false,
      forbidden: false,
    });
    try {
      const next = await connectionApi.list(workspaceId);
      if (generation.current === current) publish(workspaceId, () => next);
    } catch (cause) {
      if (generation.current !== current) return;
      setRequest({
        workspaceId,
        loading: false,
        loadingMore: false,
        forbidden: cause instanceof ConnectionApiError && cause.status === 403,
        error:
          cause instanceof Error ? cause.message : 'Connection request failed',
      });
    } finally {
      if (generation.current === current) {
        setRequest(currentRequest =>
          currentRequest.workspaceId === workspaceId
            ? { ...currentRequest, loading: false }
            : currentRequest
        );
      }
    }
  }, [workspaceId]);

  useEffect(() => {
    setSnapshot({
      workspaceId,
      page: tenantCache.get(workspaceId) ?? EMPTY_PAGE,
    });
    return subscribe(workspaceId, next =>
      setSnapshot({ workspaceId, page: next })
    );
  }, [workspaceId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const prepend = useCallback(
    (connection: WorkspaceConnectionSummary) => {
      publish(workspaceId, current => ({
        ...current,
        items: [
          connection,
          ...current.items.filter(item => item.id !== connection.id),
        ],
      }));
    },
    [workspaceId]
  );
  const remove = useCallback(
    (id: string) => {
      publish(workspaceId, current => ({
        ...current,
        items: current.items.filter(item => item.id !== id),
      }));
    },
    [workspaceId]
  );
  const replace = useCallback(
    (connection: WorkspaceConnectionSummary) => {
      publish(workspaceId, current => ({
        ...current,
        items: current.items.map(item =>
          item.id === connection.id ? connection : item
        ),
      }));
    },
    [workspaceId]
  );
  const loadMore = useCallback(async () => {
    if (!page.nextCursor) return;
    const currentGeneration = generation.current;
    const cursor = page.nextCursor;
    setRequest(current => ({
      ...(current.workspaceId === workspaceId
        ? current
        : {
            workspaceId,
            loading: false,
            loadingMore: false,
            forbidden: false,
          }),
      loadingMore: true,
      error: undefined,
    }));
    try {
      const next = await connectionApi.list(workspaceId, cursor);
      if (generation.current !== currentGeneration) return;
      publish(workspaceId, current => {
        if (current.nextCursor !== cursor) return current;
        const known = new Set(current.items.map(item => item.id));
        return {
          items: [
            ...current.items,
            ...next.items.filter(item => !known.has(item.id)),
          ],
          nextCursor: next.nextCursor,
        };
      });
    } catch (cause) {
      if (generation.current !== currentGeneration) return;
      setRequest(current =>
        current.workspaceId === workspaceId
          ? {
              ...current,
              error:
                cause instanceof Error
                  ? cause.message
                  : 'Connection request failed',
            }
          : current
      );
    } finally {
      if (generation.current === currentGeneration) {
        setRequest(current =>
          current.workspaceId === workspaceId
            ? { ...current, loadingMore: false }
            : current
        );
      }
    }
  }, [page.nextCursor, workspaceId]);

  return {
    ...page,
    loading: activeRequest.loading,
    loadingMore: activeRequest.loadingMore,
    error: activeRequest.error,
    forbidden: activeRequest.forbidden,
    reload,
    prepend,
    remove,
    replace,
    loadMore,
  };
}
